import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AxiosPromise } from 'axios';
import { VisibilityOutlined } from '@material-ui/icons';
import { generatePath, useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import openInNewTab from '../../utils/openInNewTab';

import useManuallyUpdate from '../../hooks/useManuallyUpdate';
import PermitInspectionsForm from '../../forms/permitInspectionForm/PermitInspectionsForm';
import PermitFeesForm from '../../forms/PermitFeeForm/PermitFeesForm';

import PermitActivitiesForm from '../../forms/PermitActivitiesForm';
import { DEFAULT_DATETIME_FORMAT, DEFAULT_DATE_FORMAT } from '../../constants';
import Table from '../Table';
import setColumns from './columns';
import UsersForm from '../../forms/UsersForm';
import useTablePage from '../../hooks/useTablePage';
import { convertTableBoolean } from '../../utils/convertTableValues';
import formatDate, {
  formatDateTime,
} from '../../utils/formatDate';
// import PermitPage from '../../pages/Permits/PermitPage';
// import PermitSearchPage from '../../pages/PermitSearchPage';
import { ROUTES } from '../../constants/routes';
import useUserRole from '../../hooks/useUserRole';
import { ModalDefaultDraggable } from '../../molecules';
import { updatePMResults } from '../../utils';
import TicketsForm from '../../forms/TicketsForm';
import { RootState } from '../../redux/root';
import ProjectDetailsPopup from './ProjectDetailsPopup';
import InvoiceTool from '../../Components/InvoiceTool/InvoiceTool';
import { InvoiceWrapper } from '../../forms/FeesSendForm';
import { useGetInvoiceDetailsQuery } from '../../redux/rtkQuery/apiSlice';
import useShowInvoicePdf from '../../Components/InvoiceTool/useShowInvoicePdf';
import ViewInvoiceModal from '../../Components/InvoiceTool/ViewInvoiceModal';
import { setSelectedInvoice, setPermitFees } from '../../redux/slices/feesSlice';
import { getPermitByID } from '../../api/permits';
import { getProjectByID } from '../../api/projects';

const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
});
const SearchTable = ({
  actualEndpoint,
  searchKey,
  activeTabName,
  dates,
  setDates,
}: SearchTableProps) => {
  const {
    modalError,
    modalSuccess,
    modalTitle,
    isModalOpen,
    errors,
    createOrUpdate,
    handleEdit,
    handleCloseModal,
  } = useTablePage({
    modalUpdateTitle: 'Update User',
  });

  const [
    tableManuallyUpdate,
    updateTable,
  ] = useManuallyUpdate();
  const [invoiceNumber, setInvoiceNumber] = useState<string>('');
  const { franchise, selectedFranchise } = useSelector((s:RootState) => s.userAuth.info);
  const [showPermitPage, setShowPermitPage] = React.useState(false);
  const [isEditProjectModal, setIsEditProjectModal] = useState(false);
  const [initialValues, setInitialValues] = React.useState(null);
  const [selectedFeesIds, setSelectedFeesIds] = useState([]);
  const { handleInvoiceCellClick, reimbursableAttachment } = useShowInvoicePdf(selectedFeesIds);
  const { data: existingInvoice } = useGetInvoiceDetailsQuery(invoiceNumber, { skip: invoiceNumber === '' });
  const dispatch = useDispatch();

  useEffect(() => {
    if (existingInvoice) {
      const _fees = existingInvoice.feeDetails.map(d => d.id);
      dispatch(setPermitFees(existingInvoice.feeDetails));
      setSelectedFeesIds(_fees);
    }
  }, [existingInvoice]);
  const handleEditInspections = data => {
    if (data) {
      setInitialValues(data);
      if (data.amount) {
        // this is in the case that at global search we are clicking
        // on a fee
        dispatch(setSelectedInvoice(data));
      }
      handleEdit(data);
    }
  };
  const handleCloseInspectionModal = () => {
    if (!modalError && !modalSuccess) {
      handleCloseModal();
    }
  };

  React.useEffect(() => {
    if (searchKey && activeTabName === 'permit') {
      localStorage.setItem('search', searchKey);
    }
  }, [activeTabName]);

  React.useEffect(() => {
    updateTable();
    setDates({});
  }, [actualEndpoint]);

  const {
    isSuperAdmin,
    isFranchiseAdmin,
    isEmployee,
    isInspector,
  } = useUserRole();

  const additionalActions = row => {
  if (!["employee", "inspections", "permitActivity", "invoice", "clients", "tickets"].includes(activeTabName)) return null;

  if (activeTabName === "clients") {
    return [{
      title: activeTabName,
      handler: () => handleEditInspections(row),
      Icon: VisibilityOutlined,
    }];
  }

  if (activeTabName === "employee") {
    return [{
      title: "employee",
      handler: () => handleEditInspections(row),
      Icon: VisibilityOutlined,
    }];
  }

  if (!row?.hasBranchAccess) {
    // hide eye completely
    return [];
  }

  if (activeTabName === "tickets") {
    return [{
      title: "tickets",
      handler: () => handleEditInspections(row),
      Icon: VisibilityOutlined,
    }];
  }

  return [{
    title: activeTabName,
    handler: () => handleEditInspections(row),
    Icon: VisibilityOutlined,
  }];
};

  const modifiedGetTableData = params => {
    if (activeTabName === 'clients') {
      return actualEndpoint(params).then(
        clientsResponse => ({
          ...clientsResponse,
          data: {
            ...clientsResponse.data,
            results: clientsResponse.data.results?.map(
              el => ({
                ...el,
                name: `${el?.firstName} ${el?.lastName}${
                  el?.companyName
                    ? ` (${el?.companyName})`
                    : ''
                }`,
              }),
            ),
          },
        }),
      );
    }
    if (activeTabName === 'permitActivity') {
      return actualEndpoint(params).then(
        inspectionResponse => {
          return ({
            ...inspectionResponse,
            data: {
              ...inspectionResponse.data,
              results: inspectionResponse.data.results?.map(
                el => {
                  const hasAccess = el.hasBranchAccess ?? el.has_branch_access ?? true;
                  const permitId = el.permitId ?? el.permit;
                  return {
                    ...el,
                    jurisdiction: el?.projectJurisdiction,
                    permitNumber: el?.permitNumber,
                    reviewType: el?.reviewType,
                    activityType: el?.activityTypeName,
                    activityTitle: el?.activityTitleName,
                    assignedTo: `${el?.assignedToFirstName} ${el?.assignedToLastName}`,
                    contact: el?.contactFirstName ? `${el?.contactFirstName} ${el?.contactLastName}` : "",
                    permitName: (hasAccess && permitId) ? (
                      <button
                        type="button"
                        style={{
                          color: 'inherit',
                          textDecoration: 'underline',
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          cursor: 'pointer',
                        }}
                        onClick={async () => {
                          try {
                            // Fetch permit to get projectId
                            const permitResponse = await getPermitByID(permitId);
                            const projectId = permitResponse.data.project;

                            if (projectId) {
                              const path = generatePath(ROUTES.PERMIT, {
                                projectId,
                                permitId,
                              });
                              history.push(path, { from: 'searchIntoFees' });
                            }
                          } catch (error) {
                            console.error('Error fetching permit:', error);
                          }
                        }}
                      >
                        {el.permitName}
                      </button>
                    ) : (
                      <span>{el.permitName}</span>
                    ),
                  };
                }
              ),
            },
          });
        },
      );
    }
    if (activeTabName === 'employee') {
      return actualEndpoint(params).then(
        employeeResponse => ({
          ...employeeResponse,
          data: {
            ...employeeResponse.data,
            results: employeeResponse.data.results?.map(
              el => ({
                ...el,
                userRole:
                  el?.userRole === 1 ? 'Employee' : '',
              }),
            ),
          },
        }),
      );
    }
    if (activeTabName === 'invoice') {
  const getInvoiceNumber = el => {
    if (el.invoiceNumber && el.invoiceNumber.indexOf('----') > -1) {
      return el.invoiceNumber.split('----')[0];
    }
    return el.invoiceNumber;
  };

  return actualEndpoint(params).then(invoiceResponse => ({
    ...invoiceResponse,
    data: {
      ...invoiceResponse.data,
      results: invoiceResponse.data.results?.map(el => {
        const hasAccess = el.hasBranchAccess ?? el.has_branch_access;
        const _el = {
          ...el,

          hasBranchAccess: hasAccess,

          invoicePdf: el?.invoicePdf ? `${window.location.origin}${el?.invoicePdf}` : '',
          invoiceNumber: getInvoiceNumber(el),
          fullInvoiceNumber: el.invoiceNumber,
          total: currencyFormat.format(+el.total),
          dateInvoiced: el.dateInvoiced ? formatDate(el.dateInvoiced, DEFAULT_DATE_FORMAT) : el.dateInvoiced,
          datePaid: el.datePaid ? formatDate(el.datePaid, DEFAULT_DATE_FORMAT) : el.datePaid,
          permitName: hasAccess ? (
            <button
              type="button"
              style={{
                color: 'inherit',
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
              onClick={() => {
                const path = generatePath(ROUTES.PERMIT, {
                  projectId: el.projectId,
                  permitId: el.permitId,
                });
                history.push(path, { from: 'searchIntoFees' });
              }}
            >
              {el.permitName}
            </button>
          ) : (
            <span>{el.permitName}</span>
          ),
          projectName: hasAccess ? (
            <button
              type="button"
              style={{
                color: 'inherit',
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
              }}
              onClick={async () => {
                try {
                  const projectId = el.projectId ?? el.project;
                  if (!projectId) {
                    console.error('No projectId found in data');
                    return;
                  }

                  const projectResponse = await getProjectByID(projectId);
                  const projectData = projectResponse.data;

                  if (!projectData) {
                    console.error('No project data returned from API');
                    return;
                  }

                  const _fran = franchise.find(f => f.id === selectedFranchise);
                  const frnachiseName = _fran ? _fran.name : '';

                  setInitialValues({
                    ...projectData,
                    contacts: projectData.contacts || el.permitContacts || [],
                    franchiseName: frnachiseName,
                    zipCode: projectData.zipCode || projectData.zip || "00000",
                  });

                  setTimeout(() => {
                    setIsEditProjectModal(true);
                  }, 0);
                } catch (error) {
                  console.error('Error fetching project details:', error);
                }
              }}
            >
              {el.projectName}
            </button>
          ) : (
            <span>{el.projectName}</span>
          ),
        };
        delete _el.isDeleted;
        return _el;
      }),
    },
  }));
}
    if (activeTabName === 'inspections') {
      return actualEndpoint(params).then(
        inspectionResponse => ({
          ...inspectionResponse,
          data: {
            ...inspectionResponse.data,
            results: inspectionResponse.data.results?.map(
              el => {
                const hasAccess = el.hasBranchAccess ?? el.has_branch_access ?? true;
                const permitId = el.permitId ?? el.permit;

                return {
                  ...el,
                  phoneNumber: el.contactPhoneNumber,
                  cellPhone: el.contactMobilePhoneNumber,
                  contactId: el.contact ? el.contact : "",
                  activityContact: el.contact ? `${el.contactFirstName} ${el.contactLastName}` : "",
                  inspectorName: [el.inspectorFirstName, el.inspectorLastName].filter(Boolean).join(' '),
                  permitName: (hasAccess && permitId) ? (
                    <button
                      type="button"
                      style={{
                        color: 'inherit',
                        textDecoration: 'underline',
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        cursor: 'pointer',
                      }}
                      onClick={async () => {
                        try {
                          // Fetch permit to get projectId
                          const permitResponse = await getPermitByID(permitId);
                          const projectId = permitResponse.data.project;

                          if (projectId) {
                            const path = generatePath(ROUTES.PERMIT, {
                              projectId,
                              permitId,
                            });
                            history.push(path, { from: 'searchIntoFees' });
                          }
                        } catch (error) {
                          console.error('Error fetching permit:', error);
                        }
                      }}
                    >
                      {el.permitName}
                    </button>
                  ) : (
                    <span>{el.permitName}</span>
                  ),
                };
              }
            ),
          },
        }),
      );
    }
    if (activeTabName === 'tickets') {
      return actualEndpoint(params).then(
        employeeResponse => ({
          ...employeeResponse,
          data: {
            ...employeeResponse.data,
            results: employeeResponse.data.results?.map(
              el => ({
                ...el,
                hasBranchAccess: el.hasBranchAccess ?? el.has_branch_access ?? false,
                status: el.statusTitle,
                createdAt: formatDateTime(
                  el.createdAt,
                  DEFAULT_DATETIME_FORMAT,
                ),
              }),
            ),
          },
        }),
      );
    }
    if (activeTabName === 'projectManager') {
      return actualEndpoint(params).then(pmResponse => {
        return {
        ...pmResponse,
        data: {
          ...pmResponse.data,
          results: updatePMResults(pmResponse.data.results, params),
        },
      };
      });
    }
    if (activeTabName === 'permit') {
    return actualEndpoint(params).then(permitResponse => {
      return {
        ...permitResponse,
        data: {
          ...permitResponse.data,
          results: permitResponse.data.results?.map(el => {
            const hasAccess = el.hasBranchAccess ?? el.has_branch_access;
            return {
              ...el,
              permitName: hasAccess ? (
                <button
                  type="button"
                  style={{
                    color: 'inherit',
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    const path = generatePath(ROUTES.PERMIT, {
                      projectId: el.projectId,
                      permitId: el.id,
                    });
                    history.push(path, { from: 'searchIntoFees' });
                  }}
                >
                  {el.permitName}
                </button>
              ) : (
                <span>{el.permitName}</span>
              ),
              projectName: hasAccess ? (
                <button
                  type="button"
                  style={{
                    color: 'inherit',
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                  }}
                  onClick={async () => {
                    try {
                      const projectId = el.projectId ?? el.project;
                      if (!projectId) {
                        console.error('No projectId found in data');
                        return;
                      }

                      const projectResponse = await getProjectByID(projectId);
                      const projectData = projectResponse.data;

                      if (!projectData) {
                        console.error('No project data returned from API');
                        return;
                      }

                      const _fran = franchise.find(f => f.id === selectedFranchise);
                      const frnachiseName = _fran ? _fran.name : '';

                      setInitialValues({
                        ...projectData,
                        contacts: projectData.contacts || el.permitContacts || [],
                        franchiseName: frnachiseName,
                        zipCode: projectData.zipCode || projectData.zip || "00000",
                      });

                      setTimeout(() => {
                        setIsEditProjectModal(true);
                      }, 0);
                    } catch (error) {
                      console.error('Error fetching project details:', error);
                    }
                  }}
                >
                  {el.projectName}
                </button>
              ) : (
                <span>{el.projectName}</span>
              ),
            };
          }),
        },
      };
    });
  }
    return actualEndpoint(params);
  };

  const history = useHistory();
  const [selectctedTicket, setSelectedTicket] = useState<any>({});
  const [isTicketsModalOpen, setIsTicketsModalOpen] = useState<boolean>(false);
  const editTicket = cell => {
    setIsTicketsModalOpen(true);
    setSelectedTicket(cell);
  };

  const handlePermitEdit = cell => {
      const canView = !!(cell?.hasBranchAccess ?? cell?.has_branch_access);
      if (!canView) return;

      const path = generatePath(ROUTES.PERMIT, {
        projectId: cell?.projectId,
        permitId: cell?.id,
      });
      history.push(path, { from: 'globalSearchPage' });
  };
  const cellCallbackFnIsOn = ['invoicePdf'];

  const handleCellClick = rowValue => {
    openInNewTab(rowValue?.invoicePdf);
  };

  const downloadInvoice = invoicePdf => {
    const link = document.createElement('a');
    link.href = invoicePdf;
    link.download = invoicePdf.substr(invoicePdf.lastIndexOf('/') + 1);
    link.click();
  };
  const handleProjectClick = async (data, column) => {
    if (column === "invoiceNumber") {
      const { invoicePdf, fullInvoiceNumber } = data;
      if (invoicePdf) {
        downloadInvoice(invoicePdf);
        return;
      }

      setInvoiceNumber(fullInvoiceNumber);
      handleInvoiceCellClick(data);
      return;
    }

    if (column === 'permitName') {
      // Check if user has branch access
      if (!data?.hasBranchAccess) {
        return; // Don't navigate if no access
      }

      const path = generatePath(ROUTES.PERMIT, {
        projectId: data?.projectId,
        permitId: data?.permitId,
      });
      history.push(path, { from: 'searchIntoFees' });
    }
  };

  const permitActions = row => {
      const canView = !!(row?.hasBranchAccess ?? row?.has_branch_access);

      if (!canView) return [];

      return [
        {
          title: 'View',
          handler: r => handlePermitEdit(r),
          Icon: VisibilityOutlined,
        },
      ];
  };

  const handleCloseForm = () => {
    handleCloseModal();
  };
  return (
    <>
      <InvoiceWrapper>
        <InvoiceTool fees={selectedFeesIds} reimbursableAttachment={reimbursableAttachment} />
      </InvoiceWrapper>
      <ViewInvoiceModal />
      {!showPermitPage && <Container>
        <ProjectDetailsPopup
          initialValues={initialValues}
          setIsEditProjectModal={setIsEditProjectModal}
          isEditProjectModal={isEditProjectModal}
        />

        {activeTabName !== 'permit' && activeTabName !== 'tickets' &&
          <Table
            customSearch
            initialUseSortBy={false}
            columns={setColumns(activeTabName, 'columns')}
            columnsOrder={setColumns(activeTabName, 'order')}
            manuallyUpdate={tableManuallyUpdate}
            rows={modifiedGetTableData}
            {...(isEmployee && activeTabName === 'projectManager' && { onEdit: cell => handlePermitEdit(cell) })}
            additionalRequestParams={
            dates?.fromDate && dates?.toDate
              ? {
                  search: searchKey,
                  dateFrom: formatDate(
                    dates?.fromDate,
                    'yyyy-MM-dd',
                  ),
                  dateTo: formatDate(
                    dates?.toDate,
                    'yyyy-MM-dd',
                  ),
                }
              : { search: searchKey }
          }
            handleCellClick={handleProjectClick}
            cellCallbackFnIsOn={["invoiceNumber"]}
          // {...(isEmployee && {onEdit: (cell) => handleEditClient(cell)})}
          // {...(isEmployee && {useViewModeInsteadEdit: true})}
            useViewModeInsteadEdit={isEmployee}
          // onConfigure={null}
          // onEdit={null}
            onDelete={null}
            {...(["employee", "inspections", "permitActivity", "invoice", "clients"].includes(activeTabName) ? {
            additionalActions,
           }
          : null)}
            formatValue={convertTableBoolean}
          />}

        {activeTabName === 'tickets' &&
        <Table
          customSearch
          initialUseSortBy={false}
          columns={setColumns(activeTabName, 'columns')}
          columnsOrder={setColumns(activeTabName, 'order')}
          manuallyUpdate={tableManuallyUpdate}
          rows={modifiedGetTableData}
          additionalRequestParams={
            dates?.fromDate && dates?.toDate
              ? {
                  search: searchKey,
                  dateFrom: formatDate(
                    dates?.fromDate,
                    'yyyy-MM-dd',
                  ),
                  dateTo: formatDate(
                    dates?.toDate,
                    'yyyy-MM-dd',
                  ),
                }
              : { search: searchKey }
          }
          handleCellClick={handleCellClick}
          cellCallbackFnIsOn={cellCallbackFnIsOn}
          {...(isEmployee && { onEdit: cell => editTicket(cell) })}
          // {...(isEmployee && {useViewModeInsteadEdit: true})}
          useViewModeInsteadEdit={isEmployee}
          // onConfigure={null}
          // onEdit={null}
          onDelete={null}
          {...(["employee", "inspections", "permitActivity", "invoice", "clients"].includes(activeTabName) ? {
            additionalActions,
           }
          : null)}
          formatValue={convertTableBoolean}
        />}

        {isModalOpen && initialValues && activeTabName === "employee" && (
        <ModalDefaultDraggable
          title={modalTitle}
          onClose={handleCloseModal}
          wide
        >
          <UsersForm
            initialValues={initialValues}
          // modalError={modalErrorTitle.length > 0}
            isReadOnly
            initialErrors={errors}
            onSubmit={createOrUpdate}
          />
        </ModalDefaultDraggable>
      )}
        {isModalOpen && initialValues && activeTabName === "inspections" && (
        <ModalDefaultDraggable
          title="Permit Inspections"
          onClose={handleCloseModal}
        >
          <PermitInspectionsForm
            onSubmit={createOrUpdate}
            initialValues={initialValues}
            modalError={modalError}
            updateButtonText="Update"
            initialErrors={errors}
            permit={initialValues?.permit}
            setInitialValues={setInitialValues}
            isReadOnly
          />
        </ModalDefaultDraggable>
      )}
        {isModalOpen && initialValues && activeTabName === "invoice" && (
        <ModalDefaultDraggable
          title="Invoice"
          onClose={handleCloseModal}
        >
          <PermitFeesForm
            onSubmit={createOrUpdate}
            initialValues={initialValues}
            modalError={modalError}
            addButtonText="Create"
            updateButtonText="Update"
            initialErrors={errors}
            permit={initialValues?.permitId}
            // jurisdiction={projectJurisdiction}
            handleCloseModal={handleCloseModal}
            isReadOnly
          />
        </ModalDefaultDraggable>
      )}

        {isModalOpen && initialValues && activeTabName === "clients" && (
        <ModalDefaultDraggable
          onClose={handleCloseForm}
          title={modalTitle}
          wide
        >
          <UsersForm
            searchForm
            isReadOnly
            initialValues={initialValues}
            initialErrors={errors}
            onSubmit={createOrUpdate}
          />
        </ModalDefaultDraggable>
      )}

        {isModalOpen && initialValues && activeTabName === "permitActivity" && (
        <ModalDefaultDraggable
          title="Permit Activity"
          onClose={handleCloseModal}
        >
          <PermitActivitiesForm
            onSubmit={createOrUpdate}
            initialValues={initialValues}
            modalError={modalError}
            addButtonText="Create"
            updateButtonText="Update"
            initialErrors={errors}
            permit={initialValues.permit}
            setInitialValues={setInitialValues}
            isReadOnly
          />
        </ModalDefaultDraggable>
      )}
        {activeTabName === 'permit' && (
        <Table
          customSearch
          columns={setColumns(activeTabName, 'columns')}
          rows={modifiedGetTableData}
          onDelete={null}
          columnsOrder={setColumns(activeTabName, 'order')}
          formatValue={convertTableBoolean}
          manuallyUpdate={tableManuallyUpdate}
          additionalRequestParams={
          dates?.fromDate && dates?.toDate
            ? {
                search: searchKey,
                dateFrom: formatDate(dates?.fromDate, 'yyyy-MM-dd'),
                dateTo: formatDate(dates?.toDate, 'yyyy-MM-dd'),
              }
            : { search: searchKey }
        }

          useViewModeInsteadEdit={false}
          onEdit={null}
          additionalActions={permitActions}
          handleCellClick={handleProjectClick}
          cellCallbackFnIsOn={[]}
        />
    )}
      </Container>}

      {isTicketsModalOpen && (
      <ModalDefaultDraggable
        onClose={() => setIsTicketsModalOpen(false)}
        title={modalTitle}
      >
        <TicketsForm
          permit={selectctedTicket.permitId}
          projectId={selectctedTicket.projectId}
          initialValues={selectctedTicket}
          initialErrors={errors}
          isModalError={false}
          onSubmit={() => {}}
          ticketViewOnly
        />
      </ModalDefaultDraggable>
      )}
    </>
  );
};

const Container = styled.div`
  margin: 0;
  width: 100%;
`;

interface SearchTableProps {
  actualEndpoint: Promise<AxiosPromise>;
  searchKey: string;
  activeTabName: string;
  dates: { fromDate: Date; toDate: Date };
  setDates: (data: any) => void;
}

export default SearchTable;
